import { CallcenterWizardData } from '@/types/callcenter';
import { hatPartner } from '@/utils/haushalt';

/**
 * Ja/Nein-Auswahl des Wizards → Tri-State. Arbeitskraftabsicherung nutzt
 * 'Ja'/'Nein', Sparvertraege 'ja'/'nein' — daher case-insensitiv. Alles
 * andere (vor allem die leere Auswahl '') ist "unbekannt" → null.
 */
const jaNeinToBool = (v: string | null | undefined): boolean | null => {
  const s = (v ?? '').toLowerCase();
  if (s === 'ja') return true;
  if (s === 'nein') return false;
  return null;
};

/**
 * Single Source fuer den Customer-Stammdaten-Teil des Wizard-Payloads.
 *
 * Beide Wizard-Konsumenten — `submitAndBook` (Buchung ueber
 * `/callcenter/book-for-me` bzw. `/book-meeting`) und `saveDataOnly`
 * ("Nur Daten speichern" ueber `/callcenter/save-customer`) — bauen ihr
 * Customer-Payload aus exakt diesen Feldern. Der Buchungs-Konsument
 * ergaenzt darueber nur Termin-/Slot-/Pool-/Meeting-Felder
 * (`scheduled_at`, `title`, `meeting_category`, `duration_minutes`,
 * ggf. `customer_id`/`pool_id`); der saveDataOnly-Konsument haengt
 * `intake_source: 'wizard'` an.
 *
 * Vorher gab es zwei separate Payload-Builder mit Drift (saveDataOnly
 * sandte z.B. nur 3 Partner-Felder statt 11, kein `children_birthdates`,
 * kein `device`). Diagnose A/B 2026-06-10.
 */
export function buildWizardCustomerPayload(
  wizardData: CallcenterWizardData,
  foerderanspruchTotal: number,
): Record<string, unknown> {
  // Wizard-Kirchensteuer als 'ja'/'ja_austreten' → true (Person ist
  // kirchensteuerpflichtig). 'nein' / '' → false. Das ja_austreten-
  // Spezifikum (foerderrechner-Bruecke) bleibt im Wizard-State, der
  // Customer hat nur das Boolean-Flag.
  const isChurchTaxYes = (val: string) => val === 'ja' || val === 'ja_austreten';

  // EINE Regel, geteilt mit buildRouteLeadPayload (utils/haushalt) — vorher
  // stand die Liste hier als eigene Kopie, und die Vorschau filterte gar nicht.
  const partnerData = hatPartner(wizardData.haushaltssituation) ? {
    berufsstand: wizardData.berufsstand2,
    berufsbezeichnung: wizardData.berufsbezeichnungPartner,
    geburtsjahr: wizardData.geburtsjahrPartner,
    bruttogehalt: wizardData.bruttogehalt2,
    nettogehalt: wizardData.nettogehalt2,
    krankenkasse: wizardData.krankenkassePartner,
    anrede: wizardData.anredePartner,
    vorname: wizardData.vornamePartner,
    nachname: wizardData.nachnamePartner,
    einkuenfte: wizardData.einkuenfte_partner,
    einkuenfteHoehe: wizardData.einkuenfteHoehe_partner,
    // Partner-Kirchensteuer hat im Customer-Schema keinen eigenen
    // Boolean — sie wandert ins partner_data-JSONB als Wizard-Slug.
    // HubSpot-Mapping (HS_KIRCHENSTEUER_PARTNER) liest aus partner_data.
    kirchensteuer: wizardData.kirchensteuerPartner,
  } : null;

  // Wizard hat eine einzige geteilte Notiz (siehe wizardData.notizen).
  // Verbleibt nach 2026-06-09-Verschlankung: zusammenfassung (Freitext)
  // + immobilie (strukturierter Block — Quelle fuer HubSpot-Mapper).
  // sparvertraege/sparvertraegeKinder: Arrays fuer den Backend-Mapper
  // (NOTES_JSON_EXTRACTED_KEYS → additional_info-JSONB → HubSpot).
  // Der Mapper droppt Nicht-Arrays und leere Arrays still — die
  // 'ja'/'nein'-Strings duerfen hier NICHT gesendet werden.
  const structuredNotes = JSON.stringify({
    zusammenfassung: wizardData.notizen,
    immobilie: {
      besitz: wizardData.immobilie,
    },
    sparvertraege: wizardData.sparvertraege_daten,
    sparvertraegeKinder: wizardData.sparvertraegeKinder_daten,
  });

  return {
    first_name: wizardData.vorname,
    last_name: wizardData.nachname,
    email: wizardData.email,
    phone: wizardData.telefonnummer,
    salutation: wizardData.anrede,
    birth_year: wizardData.geburtsjahr,
    occupation: wizardData.berufsstand1,
    job_title: wizardData.berufsbezeichnung,
    gross_salary: wizardData.bruttogehalt1,
    net_salary: wizardData.nettogehalt1,
    marital_status: wizardData.haushaltssituation,
    children_count: wizardData.kinder,
    children_birthdates: wizardData.kinderGeburtsjahre,
    zip_code: wizardData.plz,
    city: wizardData.ort,
    lead_source: wizardData.leadherkunft,
    device: wizardData.device,
    notes: structuredNotes,
    additional_income_type: wizardData.einkuenfte,
    additional_income_amount: wizardData.einkuenfteHoehe,
    health_insurance_provider: wizardData.krankenkasse,
    // Wizard-Notiz als eigenes Customer-Feld (customers.wizard_notes).
    // Mapper schreibt 1:1 ueber OPTIONAL_FIELD_MAP. Die notes-JSON-
    // Struktur (zusammenfassung/immobilie) wird vom Mapper nicht
    // ausgepackt — wir senden die Notiz separat als Top-Level-Feld.
    wizard_notes: wizardData.notizen,
    // Cluster 3: Felder mit vorhandener Spalte — kirchensteuer →
    // pays_church_tax (Bool), immobilie → owns_property (Bool).
    pays_church_tax: isChurchTaxYes(wizardData.kirchensteuer),
    owns_property: wizardData.immobilie === 'Ja',
    // Cluster 4: Felder ohne eigene Spalte — landen in additional_info-
    // JSONB (Mapper-Sonderpfad analog foerderanspruch_total). Keine
    // Migration noetig, konsistent zur bestehenden Konvention.
    // Tri-State (24.09.2026): leere Auswahl ist "unbekannt" und geht als
    // null raus, nicht als false. Seit dem Pro-User-Flag
    // wizard_pflichtfelder_gelockert duerfen beide Felder leer bleiben;
    // false hiesse dann faelschlich "Nein". Der Mapper schreibt null nicht
    // (isset), der Schluessel bleibt im additional_info-JSONB aus.
    has_disability_insurance: jaNeinToBool(wizardData.arbeitskraftabsicherung),
    has_savings_contracts: jaNeinToBool(wizardData.sparvertraege),
    foerderanspruch_total: foerderanspruchTotal,
    partner_data: partnerData,
  };
}
